/**
 * Which structure groups a version currently belongs to, including copies of
 * one whole-project upload that landed on several tracks.
 */

import type { ProjectFile, ProjectFileStream } from '../types';
import { groupKeyForStream, WHOLE_PROJECT_GROUP_ID } from './projectFileStreamGroups';

/** Whole-project fan-out copies are separate rows; treat uploads this close together as one release. */
export const STRUCTURE_RELEASE_MATCH_WINDOW_MS = 10 * 60 * 1000;

type ReleaseFile = Pick<
  ProjectFile,
  | 'id'
  | 'streamId'
  | 'structureReleaseId'
  | 'sharedOnStreamIds'
  | 'uploadedAsWholeProject'
  | 'name'
  | 'versionLabel'
  | 'versionNote'
  | 'detailStage'
  | 'size'
  | 'uploadedAt'
>;

type ReleaseStream = Pick<ProjectFileStream, 'id' | 'streamKind' | 'structureIds' | 'wholeProjectRoot'>;

/** Copies of one version: an explicit release id, or a whole-project upload with the same label and bytes. */
export function filesInSameStructureRelease<T extends ReleaseFile>(
  file: T,
  files: readonly T[],
  sameKindStreamIds: ReadonlySet<string>,
): T[] {
  const inKind = (item: T) => !!item.id && !!item.streamId && sameKindStreamIds.has(item.streamId);
  if (file.structureReleaseId) {
    const matched = files.filter(item => item.structureReleaseId === file.structureReleaseId && inKind(item));
    if (file.id && matched.some(item => item.id === file.id)) return matched;
    return file.id ? [file, ...matched] : matched;
  }
  if (file.uploadedAsWholeProject) {
    const at = file.uploadedAt instanceof Date ? file.uploadedAt.getTime() : 0;
    const matched = files.filter(item => {
      if (!item.uploadedAsWholeProject || !inKind(item)) return false;
      if (item.name !== file.name) return false;
      if ((item.versionLabel || '') !== (file.versionLabel || '')) return false;
      if ((item.versionNote || '') !== (file.versionNote || '')) return false;
      if ((item.detailStage || '') !== (file.detailStage || '')) return false;
      if (item.size !== file.size) return false;
      const itemAt = item.uploadedAt instanceof Date ? item.uploadedAt.getTime() : 0;
      return Math.abs(itemAt - at) <= STRUCTURE_RELEASE_MATCH_WINDOW_MS;
    });
    if (file.id && !matched.some(item => item.id === file.id) && inKind(file)) return [file, ...matched];
    return matched;
  }
  return files.filter(item => item.id && item.id === file.id);
}

/**
 * Groups to pre-check when editing a version. A shared root file is every group.
 * Copies of one release check only the groups those copies already sit on.
 */
export function structureGroupIdsForFile(
  file: ReleaseFile & Pick<ProjectFile, 'wholeProjectOrigin'>,
  args: {
    inherited: boolean;
    streams: readonly ReleaseStream[];
    files: readonly ReleaseFile[];
    groupIds: readonly string[];
  },
): string[] {
  const groupIds = args.groupIds.filter(id => id && id !== WHOLE_PROJECT_GROUP_ID);
  if (groupIds.length === 0) return [];
  const owner = args.streams.find(stream => stream.id === file.streamId);
  const onSharedRoot =
    args.inherited ||
    !!file.wholeProjectOrigin ||
    !owner ||
    owner.wholeProjectRoot === true ||
    !(owner.structureIds ?? []).length;
  if (onSharedRoot) return [...groupIds];

  const sameKind = new Set(
    args.streams.filter(stream => stream.id && stream.streamKind === owner.streamKind).map(stream => stream.id!),
  );
  const covered = new Set<string>();
  const coverStream = (streamId: string | undefined) => {
    if (!streamId) return;
    const stream = args.streams.find(item => item.id === streamId);
    if (!stream || stream.wholeProjectRoot || !(stream.structureIds ?? []).length) return;
    const key = groupKeyForStream(stream);
    if (groupIds.includes(key)) covered.add(key);
  };
  for (const member of filesInSameStructureRelease(file, args.files, sameKind)) {
    coverStream(member.streamId);
    for (const sharedId of member.sharedOnStreamIds ?? []) coverStream(sharedId);
  }
  coverStream(file.streamId);
  for (const sharedId of file.sharedOnStreamIds ?? []) coverStream(sharedId);
  if (covered.size > 0) return groupIds.filter(id => covered.has(id));
  const own = groupKeyForStream(owner);
  return groupIds.includes(own) ? [own] : [];
}
